import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ActivatedRoute, Data, NavigationStart, Params, Router } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { BehaviorSubject, noop, Observable, of, zip } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import {
  Dictionary,
  FilterListItemChangeArgs,
  FilterListItemModel,
  FilterListItemValueModel,
  RedirectItem,
  TsOverlayComponent,
  TsPaginatorChangeEvent,
  TsSnackbarService,
  TsTableService,
} from 'tsui';
import cloneDeep from 'lodash-es/cloneDeep';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Actions, ofActionCompleted, ofActionDispatched, Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { Project, ProjectTemplate, Property } from '@modules/projects/projects.types';
import { PropertiesService } from '@modules/properties/properties.service';
import { Account, FieldDef, QueryConfig, SavedView } from '@core/models';
import UrlUtils from '@shared/Utils/url.utils';
import { PropertyDealAddComponent } from '@modules/properties/modals/deal-add/deal-add.component';
import { getAllowedFields, removeArchivedFieldsFromTableDefinition } from '@shared/functions';
import { FileFormat, RelatedToType } from '@shared/enums';
import { LayoutService } from 'app/layout/layout.service';
import { CustomFormOptions, CustomField } from '@shared/modules/custom-form/custom-form.types';
import { CustomFormComponent } from '@shared/modules/custom-form/custom-form/custom-form.component';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { GraphqlConnection, GraphqlResultPageInfo } from '@shared/models/graphql';
import { Properties } from '../../state/actions';
import { PropertiesState } from '../../state/state';
import { ChangeType, OriginalStates, TableSortState } from '@shared/models';
import { LookupService } from '@services/lookup.service';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { TsFilterUtilsService } from '@services/ts-filter-utils.service';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { ProjectsService } from '@modules/projects/projects.service';
import { FieldDefsState } from '@state/fields/state';
import { GridApi, SortChangedEvent } from 'ag-grid-enterprise';
import { nonEditableStyle } from 'tsui/lib/cross-cutting/util';
import { SavedViewUtilsService } from '@services/saved-view-utils.service';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { UserState } from '@state/user/state';
import { AccountActions } from '@state/account/actions';

type GraphqlResult<T> = { rows: T[]; pageInfo?: GraphqlResultPageInfo };

@Component({
  selector: 'properties-list-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss', '../../../../shared/styles/ts-page-header-list.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertiesListTableComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  @ViewChild(MatSort) private sort: MatSort;
  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;

  private queryFilters: Partial<QueryConfig.QueryFilters>;
  private _changeTriggers: Set<string> = new Set();
  private _originalStates: OriginalStates;
  private fieldDefs: FieldDef[] = [];
  private propertyTemplates: ProjectTemplate<any>[];

  sortState: TableSortState = {
    active: 'name',
    direction: 'asc',
  };
  previousSelection: any[] = [];
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  properties: Property[];
  isLoading = signal(false);
  exportingCsv: boolean = false;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  filteredFieldDefs: FieldDef[] = [];
  routeData: Data;
  account: Account;
  readonly savedView: WritableSignal<SavedView> = signal(undefined);
  selection = new SelectionModel<Property>(true, []);
  pageInfo: GraphqlResultPageInfo;
  pageSize = 20;
  Permission = Permission;
  NewPermission = NewPermission;
  searchInputControl: FormControl = new FormControl('');
  columnDefs: ColumnDef[] = [];
  tsActions: TableActions[] = [];
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this._projectsService.getProjectByIdBase(String(key)).pipe(
          untilComponentDestroyed(this),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    } as RedirectItem,
  };
  newTable: boolean = false;
  gridApi: GridApi<any>;
  graphqlResults$: Observable<GraphqlResult<Property>> = inject(Store).select(PropertiesState.graphqlResults);
  viewManagementOptions: Signal<TsMenuItem[]> = computed(() => {
    if (this.savedView()) {
      return this.savedViewUtilsService.getViewManagementMenuOptions(
        this.savedView,
        RelatedToType.Property,
        this.isLoading,
      );
    }
  });

  readonly nestedFiltersEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));

  titleField: FieldDef;

  private get gqlConnection(): string {
    return this.nestedFiltersEnabled ? GraphqlConnection.propertiesSqlBased : GraphqlConnection.properties;
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _propertiesService: PropertiesService,
    private readonly _router: Router,
    private readonly _accountService: AccountService,
    private readonly _matDialog: MatDialog,
    private readonly _snackBar: TsSnackbarService,
    private readonly store: Store,
    private readonly layoutService: LayoutService,
    private readonly actions$: Actions,
    public readonly lookupService: LookupService,
    private readonly tsFilterUtilsService: TsFilterUtilsService,
    private readonly _projectsService: ProjectsService,
    private readonly savedViewUtilsService: SavedViewUtilsService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);

    this.relatedToRedirectConfig = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);
  }

  ngOnInit(): void {
    this.fetchRouteData();
    this.setFilteredFields();
    this.setupFiltersAndSavedViews();
    this.fillPageSize();
    this.setupState();
    this.closeSnackBarOnLeave();
    this.searchInputWatcher();
    this.checkTable();
    this.getColDefs();
  }

  ngOnDestroy(): void {
    this._snackBar.dismiss();
  }

  private searchInputWatcher(): void {
    this.searchInputControl.valueChanges
      .pipe(untilComponentDestroyed(this), TsRxjsOperators.memoizedDebounceTime(500))
      .subscribe((query) => {
        this.queryFilters = { ...this.queryFilters, q: query };
        this.graphql(0);
      });
  }

  private fetchRouteData(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.routeData = data;
      this.propertyTemplates = this.routeData.options.project_templates
        .filter(
          (template) => template.related_to_type == RelatedToType.Property && (!template.hidden || template.default),
        )
        .sort((a: ProjectTemplate<any>, b: ProjectTemplate<any>) => a.name.localeCompare(b.name));
    });
  }

  private setFilteredFields(): void {
    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Property, {
        allowedFieldsOnly: true,
        addNestedFields: this.nestedFiltersEnabled,
      }),
    );

    this.filteredFieldDefs = cloneDeep(this.fieldDefs);

    const index = this.filteredFieldDefs.findIndex((item) => item.name === 'name');
    if (index > -1) this.filteredFieldDefs.splice(index, 1);

    this.setFilterNestedFields();
    this.titleField = this.lookupField('name');
  }

  private setFilterNestedFields(): void {
    this.typeFields.next(this.tsFilterUtilsService.getFilterList(this.fieldDefs));
  }

  private setupFiltersAndSavedViews(): void {
    this._activatedRoute.data
      .pipe(
        untilComponentDestroyed(this),
        switchMap((data: Data) => {
          return zip(of(data.savedView) as Observable<SavedView>, this._activatedRoute.queryParams);
        }),
        tap(([savedView, params]: [SavedView, Params]) => {
          this.searchInputControl.setValue('', { emitEvent: false });

          let filter: any;

          if (params['filter']) {
            filter = JSON.parse(params['filter']);
          }

          if (savedView.related_to_type !== RelatedToType.Property) {
            localStorage.removeItem('propertiesSavedViewId');
            this._router.navigate(['/properties']);
            return;
          }

          if (savedView) {
            this.savedView.set(savedView);
            this.displayColumns = this.getDisplayColumnsState();
            this.cleanseDisplayColumns();
            this.sortState = this.getSortState();
            if (filter) {
              this.queryFilters = filter;
              filter = null;
            } else if (this.savedView().saved_filter) {
              this.queryFilters = this.getQueryFiltersState();
              this.searchInputControl.setValue(this.queryFilters?.q, { emitEvent: false });
              filter = null;
            }

            this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));

            UrlUtils.saveToLocalStorage('propertiesSavedViewId', savedView.id);
            const currentPage = +sessionStorage.getItem('propertiesCurrentPage') || 0;

            this.graphql(currentPage);
          }
          this.savedView.set(savedView);
        }),
      )
      .subscribe(noop);
  }

  private fillPageSize(): void {
    const pageSize = UrlUtils.getFromLocalStorage('propertiesPageSize');
    if (!pageSize) {
      UrlUtils.saveToLocalStorage('propertiesPageSize', this.pageSize);
    } else {
      this.pageSize = Number(pageSize);
    }
  }

  private setupState(): void {
    this.graphqlResults$
      .pipe(
        untilComponentDestroyed(this),
        tap((result) => {
          this.properties = result?.rows ?? [];
          this.pageInfo = result?.pageInfo;
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionDispatched(Properties.Graphql.Action),
        tap(() => {
          this.isLoading.set(true);
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(Properties.Graphql.Action),
        tap(() => {
          this.isLoading.set(false);
          this.exportingCsv = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  private closeSnackBarOnLeave(): void {
    this._router.events
      .pipe(
        untilComponentDestroyed(this),
        filter((route) => route instanceof NavigationStart),
      )
      .subscribe(() => this._snackBar.dismiss());
  }

  /**
   * SC-49680: Update the account definitions by deleting the outdated 'property' definition used for the add form.
   * Update the saved view definitions to include the new fields from the add form.
   * @param definitions - The new definitions to update the account with
   * @param savedView - The saved view to update the definition of
   */
  private updateCustomFormDefinition(definitions: Pick<Account, 'definitions'>, savedView: SavedView): void {
    this.account.definitions = definitions;

    this._accountService
      .updateSavedView(savedView)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => {
          // SC-49680: Remove the property definition from the account definitions if it exists
          if (this.account.definitions?.property) {
            delete this.account.definitions.property;
            return this.store.dispatch(
              new AccountActions.UpdateAccount({
                id: this.account.id,
                configuration: {
                  definitions: JSON.stringify(this.account.definitions),
                },
              }),
            );
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.account = this.store.selectSnapshot(AccountState.getAccount);
        this._changeDetectorRef.detectChanges();
      });
  }

  private generateDisplayColumnPreservingOrder(displayColumns: string[], fieldName: string): string[] {
    if (displayColumns[0] === 'name') {
      return [displayColumns[0], fieldName, ...displayColumns.slice(1)];
    } else {
      return [fieldName, ...displayColumns];
    }
  }

  private saveTableSort(): void {
    this.savedViewUtilsService.saveTableSort(this.savedView, this.sortState);
  }

  private saveColumns(): void {
    this.savedView.update((sv) => {
      return {
        ...sv,
        definition: JSON.stringify({
          table: { fields: this.displayColumns, sort: JSON.parse(sv.definition).table?.sort },
        }),
      };
    });

    this._accountService.updateSavedView(this.savedView()).pipe(untilComponentDestroyed(this)).subscribe(noop);
  }

  private cleanseDisplayColumns(): void {
    const index = this.displayColumns.findIndex((item) => item === 'name');
    if (index > -1) {
      this.displayColumns.splice(index, 1);
    }
    this.loopDisplayColumns = [...this.displayColumns];
    this.loopDisplayColumns.unshift('name');
    this.loopDisplayColumns.unshift('checkbox');
  }

  private removeDisplayColumn(fieldName: string): void {
    this.displayColumns.splice(
      this.displayColumns.findIndex((column) => column === fieldName),
      1,
    );
    this.loopDisplayColumns.splice(
      this.loopDisplayColumns.findIndex((column) => column === fieldName),
      1,
    );
    this.cleanseDisplayColumns();
    this.getColDefs();
    this._changeDetectorRef.markForCheck();
  }

  private addDisplayColumn(fieldName: string): void {
    if (!this.displayColumns.includes(fieldName)) {
      this.displayColumns = this.generateDisplayColumnPreservingOrder(this.displayColumns, fieldName);
      this.cleanseDisplayColumns();
    }
    this.getColDefs();
    this._changeDetectorRef.markForCheck();
  }

  private graphql(currentPage: number, search: string = null, forExport = false): void {
    let payload: Properties.Graphql.Params = {
      connection: this.gqlConnection,
      fields: this.displayColumns ?? [],
      fieldDefs: this.fieldDefs,
      filter: {
        fjson: this.queryFilters?.fjson ?? '[]',
        query: search ? search : this.queryFilters?.q ? this.queryFilters.q : '',
        pagination: { page: currentPage, size: this.pageSize },
        sort: [
          {
            field: this.sortState.active,
            order: this.sortState.direction,
          },
        ],
      },
      options: {
        keepConnectionCase: true,
      },
    };

    if (forExport) {
      payload = {
        ...payload,
        filter: { ...payload.filter, pagination: { page: 0, size: 50000 } },
        exportOptions: {
          format: FileFormat.CSV,
          fileName: `${this.savedView().name}`,
          savedViewId: this.savedView()?.id.toString(),
        },
      };
    }

    this.store.dispatch(new Properties.Graphql.Action(payload));
    this.getColDefs();
  }

  private captureOriginalStates(): void {
    this._originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      sort: this.getSortState(),
      queryFilters: { ...this.getQueryFiltersState() },
      filterValueList: this.getFilterValueListState(),
    };
  }

  private getSortState(): TableSortState {
    const savedSort = JSON.parse(this.savedView()?.definition)?.table?.sort;

    return {
      active: savedSort?.active || 'name',
      direction: savedSort?.direction || 'asc',
    };
  }

  private setSortState(value: TableSortState): void {
    this._originalStates.sort = {
      active: value.active,
      direction: value.direction,
    };
  }

  private getDisplayColumnsState(): string[] {
    return removeArchivedFieldsFromTableDefinition(
      JSON.parse(this.savedView()?.definition)?.table?.fields || [],
      this.fieldDefs,
    );
  }

  private setDisplayColumnsState(value: string[]) {
    this._originalStates.displayColumns = value;
  }

  private getFilterValueListState(): FilterListItemValueModel[] {
    return this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
  }

  private setFilterValueListState(value: FilterListItemValueModel[]) {
    this._originalStates.filterValueList = value;
  }

  private getQueryFiltersState(): Partial<QueryConfig.QueryFilters> {
    return JSON.parse(this.savedView().saved_filter?.filters || '{}');
  }

  private setQueryFiltersState(value: Partial<QueryConfig.QueryFilters>) {
    this._originalStates.queryFilters = value;
  }

  private openTrackStateSnackbar(changeType: ChangeType): void {
    this._changeTriggers.add(changeType);
    this.captureOriginalStates();
    this._snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
  }

  private clearChangeTriggers(): void {
    this._changeTriggers.clear();
  }

  private resetToOriginalStates(): void {
    if (this._changeTriggers.has('columns')) {
      this.displayColumns = this.getDisplayColumnsState();
      this.cleanseDisplayColumns();
      this.getColDefs();
    }

    if (this._changeTriggers.has('sort')) {
      this.sortState = this.getSortState();
      this.graphql(this.pageInfo?.currentPage || 0);
    }

    if (this._changeTriggers.has('filters')) {
      this.queryFilters = this.getQueryFiltersState();
      this.filterValueList.next(this.getFilterValueListState());
      this.onCloseByBackdropClick();
      this.graphql(this.pageInfo.currentPage);
    }

    this.clearChangeTriggers();
    this._changeDetectorRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this._changeTriggers.has('columns')) {
      this.setDisplayColumnsState(this.displayColumns);
      this.saveColumns();
    }

    if (this._changeTriggers.has('sort')) {
      this.setSortState(this.sortState);
      this.saveTableSort();
    }

    if (this._changeTriggers.has('filters')) {
      this.setQueryFiltersState(this.queryFilters);
      this.setFilterValueListState(this.filterValueList.value);
      this.saveFilter(this.queryFilters);
    }

    this.clearChangeTriggers();
  }

  private saveFilter(filters: Partial<QueryConfig.QueryFilters>): void {
    if (this.savedView()) {
      this.savedView().saved_filter.filters = JSON.stringify({ ...filters, q: '' });

      if (!this.savedView().saved_filter.id) {
        this._accountService
          .saveFilter(this.savedView().saved_filter)
          .pipe(untilComponentDestroyed(this))
          .subscribe((savedFilter) => {
            let url = this._router.url.split('?')[0];
            this._router.navigate([url], { queryParams: { filterId: savedFilter.id } });
          });
      } else {
        this._accountService
          .updateFilter(this.savedView().saved_filter)
          .pipe(untilComponentDestroyed(this))
          .subscribe(noop);
      }
    }
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  public exportPropertiesAsCSV(): void {
    this.exportingCsv = true;

    let sortField = this.sortState.active;

    let field = this.lookupField(sortField);
    if (field) {
      if (field.meta && field.meta.sorting) {
        sortField = field.meta.sorting;
      }
    }

    this.graphql(0, '', true);
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.properties?.length;
    return numSelected === numRows;
  }

  public masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.properties.forEach((row) => this.selection.select(row));
  }

  public createProperty(): void {
    const allowedFields = getAllowedFields(this.fieldDefs, RelatedToType.Property).filter(
      (field) => !field.meta.readonly && field.name !== 'project_ids' && field.name !== 'title',
    );

    const selectedFields = (
      JSON.parse(this.savedView().definition)?.add_form_definition?.fields
        ? JSON.parse(this.savedView().definition).add_form_definition.fields
        : [
            { name: 'name', isRequired: true, isRemovable: false },
            { name: 'property_type_id', isRequired: true, isRemovable: false },
            { name: 'template_id', isRequired: true, isRemovable: false },
            { name: 'cff_address', isRequired: false, isRemovable: true },
          ]
    ).filter((x) => ['template_id', 'cff_address'].includes(x.name) || allowedFields.find((y) => y.name === x.name));

    this.layoutService.openComponentDrawer<CustomFormOptions>(CustomFormComponent, {
      inputs: {
        relatedToType: RelatedToType.Property,
        renderAttachFileField: false,
        allowedFields,
        selectedFields,
        dataSources: { template_id: this.propertyTemplates },
        enableEditing: this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_MANAGE_ADD_FORM)),
        commonData: {
          formFieldData: this.routeData,
          disableItemSuggestion: false,
          existingProperties: this.properties,
        },
        saveAction: (property: Property) => {
          this._propertiesService
            .saveProperty(property as Property)
            .pipe(untilComponentDestroyed(this))
            .subscribe({
              next: (newProperty) => {
                this._snackBar.open('Property successfully created', 'success');
                this._router.navigateByUrl(`/properties/${newProperty.id}/details/overview`);
              },
              error: () => {
                this._snackBar.open('Unable to create a new property at this time. Please try again later.', 'error');
              },
              complete: () => {
                this.layoutService.closeComponentDrawer();
                this._changeDetectorRef.detectChanges();
              },
            });
        },
      },
      outputs: {
        onTemplateChanged: (fields: CustomField[]) => {
          // SC-49680: Update the saved view definition to include the new fields
          this.savedView.update((sv) => {
            return {
              ...sv,
              definition: JSON.stringify({
                ...JSON.parse(sv.definition),
                add_form_definition: {
                  fields,
                },
              }),
            };
          });

          this.updateCustomFormDefinition(this.account.definitions, this.savedView());
        },
        onNavigationByUrlRequested: (url: string) => this._router.navigateByUrl(url),
      },
    });
  }

  public createPostalDealFromSelectedProperties(dealType: 'single' | 'portfolio'): void {
    if (this.selection.selected.length === 1) dealType = 'single';
    this._matDialog
      .open(PropertyDealAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        width: '40vw',
        disableClose: true,
        data: { data: this.routeData, selectedProperties: this.selection, dealType: dealType },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  public createDealFromSelectedProperties(dealType: 'single' | 'portfolio'): void {
    this._matDialog
      .open(PropertyDealAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        width: '40vw',
        disableClose: true,
        data: { data: this.routeData, selectedProperties: this.selection, dealType: dealType },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  public filterFields(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.label.toLowerCase().includes(value));
  }

  public filterFieldsInputKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;

    const fieldDef = this.filteredFieldDefs[0];
    const isFieldVisible = this.fieldDefs.find((item) => item.name === fieldDef.name);

    if (isFieldVisible) this.removeDisplayColumn(fieldDef.name);
    else this.addDisplayColumn(fieldDef.name);
  }

  public toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    this.openTrackStateSnackbar('columns');

    if (change.checked) {
      this.addDisplayColumn(fieldName);
    } else {
      this.removeDisplayColumn(fieldName);
    }

    this.graphql(this.pageInfo.currentPage);
  }

  public lookupField(name: string): FieldDef & { groupLabel?: string } {
    const field = this.fieldDefs.find((fieldDef) => fieldDef.name == name);

    if (this.nestedFiltersEnabled && field.related_to_type !== RelatedToType.Property) {
      const [rootRelatedType] = name.split('.');
      const rootFieldDef = this.fieldDefs.find((fieldDef) => fieldDef.name == rootRelatedType);
      return { ...field, groupLabel: rootFieldDef?.label };
    }

    return field;
  }

  public dropColumns(event: CdkDragDrop<string[]>): void {
    this.openTrackStateSnackbar('columns');

    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
      this.cleanseDisplayColumns();
    }
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(
      event,
      this.columnDefs.map((x) => x.name.toLowerCase()),
      ['openDetails', 'checkbox'],
      (columns) => {
        this.columnDefs = columns.map((column) => this.lookupField(column));
        this.displayColumns = columns;
        this.cleanseDisplayColumns();
        this.getColDefs();
        this.openTrackStateSnackbar('columns');
      },
    );
  }

  public onUpdate(params, property: Property): void {
    const options = { ...params, id: property?.id };
    const selectColumn = [];
    selectColumn.push(Object.keys(params)[0]);
    if (params[Object.keys(params)[0]] !== 'add_new') {
      this._propertiesService.saveProperty(options, property).pipe(untilComponentDestroyed(this)).subscribe();
    } else {
      this.graphql(this.pageInfo.currentPage);
    }
  }

  public onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  public trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  public pageChanged(event: TsPaginatorChangeEvent): void {
    this.pageSize = event.pageSize;
    this.pageInfo = { ...this.pageInfo, pageSize: this.pageSize, currentPage: event.activePage };
    UrlUtils.saveToLocalStorage('propertiesPageSize', String(this.pageSize));
    this.graphql(this.pageInfo.currentPage);
  }

  public sortChanged(event: MatSort): void {
    this.sort = event;
    this.sortState = {
      active: this.sort.active,
      direction: this.sort.direction as 'asc' | 'desc',
    };
    this.graphql(this.pageInfo?.currentPage || 0);
    this.openTrackStateSnackbar('sort');
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { fjson: `[]`, q: '' };
    this.graphql(this.pageInfo.currentPage);
  }

  public onFilterApplied(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
      q: '',
    };

    this.graphql(0);

    this._filtersOverlayBox.closeOverlay();
  }

  private getColDefs(): void {
    if (this.newTable) {
      this.columnDefs = this.displayColumns.map((column) => this.lookupField(column));
      this.setColumnsAttributes();
    }
  }

  private setColumnsAttributes() {
    this.columnDefs.unshift({
      name: 'Name',
      label: 'Name',
      type: 'text',
      tsTableOptions: {
        suppressMovable: true,
        rowDrag: false,
        field: 'name',
        pinned: 'left',
        editable: true,
      },
    });
    this.columnDefs.map((fieldDef, index) => {
      if (fieldDef) {
        if (fieldDef.meta?.record_type === RelatedToType.User) {
          fieldDef.meta.options = this.store
            //@ts-ignore
            .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
            .map((x) => ({ id: x.id, label: x.name }));
        }

        fieldDef.tsTableOptions = {
          sortable: true,
          resizable: true,
          sort: this.sortState.active === fieldDef.name ? this.sortState.direction : null,
          editable: !fieldDef.meta?.readonly,
          cellStyle: nonEditableStyle(fieldDef),
          ...fieldDef.tsTableOptions,
        };
      }
    });
  }

  onSelectionChanged(event) {
    if (!event.length) {
      this.selection.clear();
      this.previousSelection = [];
      return;
    }

    if (event.length === this.properties.length) {
      this.masterToggle();
    } else {
      const selectedDifference = event.filter((item) => !this.previousSelection.includes(item));
      const deselectedDifference = this.previousSelection.filter((item) => !event.includes(item));

      selectedDifference.forEach((item) => {
        this.selection.select(item);
      });

      deselectedDifference.forEach((item) => {
        this.selection.deselect(item);
      });

      this.previousSelection = [...event];
    }
  }

  onSortChanged(event: SortChangedEvent): void {
    if (event.columns && event.columns.length > 0) {
      const sortedColumn = event.columns.filter((col) => col.getSort());
      const columnDef = this.columnDefs.find((def) => def.name === sortedColumn[0]?.getColDef().field);
      this.sortState = {
        active: columnDef?.meta?.sorting || sortedColumn[0]?.getColDef().field || 'name',
        direction: sortedColumn[0]?.getSort() || 'asc',
      };
    } else {
      this.sortState = {
        active: 'name',
        direction: 'asc',
      };
    }

    this.graphql(this.pageInfo?.currentPage || 0);
    this.openTrackStateSnackbar('sort');
  }

  updateProperty(property: Property): void {
    this._propertiesService.saveProperty(property).pipe(untilComponentDestroyed(this)).subscribe();
  }

  onCellEditingStopped(event) {
    const editedData = event.data;
    this.updateProperty(editedData);
  }

  private checkTable(): void {
    this.newTable = this.account.features.includes(FeatureFlag.NEW_TABLE);
  }

  public goToProperty(id: string): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this._router.navigate([`../../${id}`], { relativeTo: route });
    this._changeDetectorRef.markForCheck();
  }

  public gridReady(params) {
    this.gridApi = params.api;
  }

  getCreatePropertyPermission(): NewPermission | Permission {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return NewPermission.PROPERTIES_CREATE;
    } else {
      return Permission.PROPERTY_CREATE;
    }
  }
}
